import { useState, useEffect } from "react"
import { string, func, number, bool } from "prop-types"
import { css } from "@emotion/core"
import { isBlank } from "utils/helpers"
import { StackView } from "@planningcenter/tapestry-react"

const numberSpinnerStyle = css`
  align-items: stretch;
  background: var(--text-input--background-color);
  border-radius: 4px;
  border: 1px solid var(--text-input--border-color);
  display: flex;
  justify-content: space-between;
  height: 40px;
  width: 112px;
  flex-shrink: 0;
  padding: 0;
`
const numberSpinnerInputStyle = css`
  background: transparent !important;
  box-shadow: none !important;
  --text-input--border-width: 0;
  height: 100%;
  padding: 0;
  width: 34px;
  text-align: center;
  -moz-appearance: textfield;

  &:disabled {
    color: var(--t-fill-color-neutral-040);
    cursor: not-allowed;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
`
const numberSpinnerButtonStyle = css`
  background: transparent;
  border: 0;
  color: var(--t-fill-color-neutral-010);
  cursor: pointer;
  font-size: 20px;
  font-weight: 600;
  height: auto;
  margin-top: -1px;
  padding: 0 0.75rem;

  &:disabled {
    color: var(--t-fill-color-neutral-030);
    cursor: not-allowed;
  }
`

const NumberSpinner = ({
  onChange,
  name = "numberSpinner",
  disabled = false,
  min = 0,
  max,
  initialValue = 0,
  showInput = true,
  step = 1
}) => {
  const [value, setValue] = useState(initialValue || min)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const handleChange = ({ target: { value: inputValue } }) => {
    if (inputValue === "") return setValue(inputValue)

    let newValue = Number(inputValue)

    if (newValue < min) {
      newValue = min
    }

    if (!isBlank(max) && newValue > max) {
      newValue = max
    }

    setValue(newValue)
    onChange(newValue)
  }

  const handleDecrement = _e => {
    const newValue = value === min ? value : value - step
    setValue(newValue)
    onChange(newValue)
  }

  const handleIncrement = _e => {
    const newValue = !isBlank(max) && value === max ? value : value + step
    setValue(newValue)
    onChange(newValue)
  }

  return (
    <div css={numberSpinnerStyle}>
      <button
        disabled={disabled || value === min}
        onClick={handleDecrement}
        aria-label="decrement input value"
        css={numberSpinnerButtonStyle}
      >
        -
      </button>
      <StackView distribution="center" css={numberSpinnerInputStyle}>
        <input
          name={name}
          id={name}
          type={showInput ? "number" : "hidden"}
          disabled={disabled}
          max={max}
          min={min}
          step={step}
          value={value}
          onChange={handleChange}
          css={numberSpinnerInputStyle}
        />
        {!showInput && <>{value}</>}
      </StackView>
      <button
        disabled={disabled || (!isBlank(max) && value >= max)}
        onClick={handleIncrement}
        aria-label="increment input value"
        css={numberSpinnerButtonStyle}
      >
        +
      </button>
    </div>
  )
}

NumberSpinner.propTypes = {
  onChange: func.isRequired,
  name: string,
  disabled: bool,
  max: number,
  min: number,
  step: number,
  showInput: bool,
  initialValue: number,
  variant: string
}

export default NumberSpinner
